// @givmo/board — the self-assessment's one answer form (CBG-YR4-ASSESS-WEB, frame s2): the person's
// own page and the officer's paper answer sheet alike, the annual questionnaire's precedent (one form
// for both roads, `conflicts/coi-answer-form.tsx`).
//
// The questions are the assessment's as frozen at its opening, in their groups and order, each
// statement with its source and the five answers the backend serves, in their order; a comment per
// group under the served prompt and help; then the open questions in the person's own words. Every
// word of them is the backend's (approved, CBG-YR4-COPY1). Answering is optional statement by
// statement: nothing here is required, and nothing scores or weighs an answer.
//
// The form holds no state of its own: its host keeps the draft (the person's page keeps it in this tab
// until sent, `director-drafts.ts`; the paper dialog only while it is open).

import { Card, CardBody, RadioTile, RadioTileGroup, Textarea, cn } from '@givmo/design-system';
import type {
  BoardSelfAssessmentAnswerOptionOut,
  BoardSelfAssessmentGroupOut,
  BoardSelfAssessmentOpenQuestionOut,
} from '@givmo/api-client';
import { GroupLabel } from '../../components/fine-print';
import { SourceLine } from '../conflicts/coi-parts';
import type { AnswerDraft } from './assessment-decode';

/** The comment and open answers' bound (`BoardSelfAssessmentAnswersIn`, §4.4). */
export const ANSWER_TEXT_MAX_LENGTH = 2000;

const STATEMENT = 'font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-strong';

export interface AssessmentQuestions {
  groups: readonly BoardSelfAssessmentGroupOut[];
  answer_options: readonly BoardSelfAssessmentAnswerOptionOut[];
  comment_prompt: string;
  comment_help: string;
  open_questions?: readonly BoardSelfAssessmentOpenQuestionOut[] | null;
  open_questions_title?: string | null;
}

export function AssessmentAnswerForm({
  questions,
  draft,
  onChange,
  disabled = false,
  framing,
  testId,
}: {
  questions: AssessmentQuestions;
  draft: AnswerDraft;
  onChange: (next: AnswerDraft) => void;
  disabled?: boolean;
  /** `cards`: a card per group (the person's page, s2); `sections`: ruled sections (a dialog). */
  framing: 'cards' | 'sections';
  testId: string;
}) {
  const setRating = (statementId: string, answer: string) =>
    onChange({ ...draft, ratings: { ...draft.ratings, [statementId]: answer } });
  const setComment = (groupId: string, text: string) =>
    onChange({ ...draft, comments: { ...draft.comments, [groupId]: text } });
  const setOpenAnswer = (questionId: string, text: string) =>
    onChange({ ...draft, openAnswers: { ...draft.openAnswers, [questionId]: text } });
  const openQuestions = questions.open_questions ?? [];

  const parts = [
    ...questions.groups.map((group) => ({
      key: group.group_id,
      testId: `${testId}-group-${group.group_id}`,
      body: (
        <>
          <GroupLabel>{group.title}</GroupLabel>
          {group.statements.map((statement) => (
            <div
              key={statement.statement_id}
              className="flex flex-col gap-8"
              data-testid={`${testId}-${statement.statement_id}`}
            >
              <div className="flex flex-col gap-2">
                <p className={STATEMENT}>{statement.text}</p>
                {statement.source ? <SourceLine source={statement.source} /> : null}
              </div>
              <RadioTileGroup
                value={draft.ratings[statement.statement_id] ?? ''}
                onValueChange={(answer) => setRating(statement.statement_id, answer)}
                disabled={disabled}
                aria-label={statement.text}
                className="sm:flex-row sm:flex-wrap sm:gap-x-28 sm:gap-y-6"
                testId={`${testId}-${statement.statement_id}-answer`}
              >
                {questions.answer_options.map((option) => (
                  <RadioTile
                    key={option.answer}
                    value={option.answer}
                    label={option.label}
                    testId={`${testId}-${statement.statement_id}-${option.answer}`}
                  />
                ))}
              </RadioTileGroup>
            </div>
          ))}
          <Textarea
            label={questions.comment_prompt}
            helper={questions.comment_help}
            rows={2}
            maxLength={ANSWER_TEXT_MAX_LENGTH}
            value={draft.comments[group.group_id] ?? ''}
            onChange={(event) => setComment(group.group_id, event.target.value)}
            disabled={disabled}
            testId={`${testId}-comment-${group.group_id}`}
          />
        </>
      ),
    })),
    ...(openQuestions.length > 0
      ? [
          {
            key: 'open',
            testId: `${testId}-open`,
            body: (
              <>
                {questions.open_questions_title ? (
                  <GroupLabel>{questions.open_questions_title}</GroupLabel>
                ) : null}
                {openQuestions.map((question) => (
                  <Textarea
                    key={question.question_id}
                    label={question.text}
                    rows={2}
                    maxLength={ANSWER_TEXT_MAX_LENGTH}
                    value={draft.openAnswers[question.question_id] ?? ''}
                    onChange={(event) => setOpenAnswer(question.question_id, event.target.value)}
                    disabled={disabled}
                    testId={`${testId}-open-${question.question_id}`}
                  />
                ))}
              </>
            ),
          },
        ]
      : []),
  ];

  if (framing === 'cards') {
    return (
      <>
        {parts.map((part) => (
          <Card key={part.key} testId={part.testId}>
            <CardBody className="flex flex-col gap-16">{part.body}</CardBody>
          </Card>
        ))}
      </>
    );
  }
  return (
    <div className="flex flex-col" data-testid={testId}>
      {parts.map((part, index) => (
        <section
          key={part.key}
          className={cn(
            'flex flex-col gap-14',
            index > 0 && 'mt-20 border-t border-border-subtle pt-20',
          )}
          data-testid={part.testId}
        >
          {part.body}
        </section>
      ))}
    </div>
  );
}
